import {Header} from "@/components/Header";
import * as React from "react";
import {useRef, useState} from "react";
import {AccountContext} from "@/context/AccountProvider";
import {t} from "i18next";
import {AddProfessionForm} from "@/components/AddProfessionForm";
import {AddEducationForm} from "@/components/AddEducationForm";
import {AddAwardForm} from "@/components/AddAwardForm";
import {EditProfileForm} from "@/components/EditProfileForm";
import {updateProfilePicture} from "@/service/accountApi";
import {Modal} from "@/components/Modal";
import {AddSocialMediaForm} from "@/components/AddSocialMediaForm";
import {AddMedicalConditionForm} from "@/components/AddMedicalConditionForm";
import {EditEducationForm} from "@/components/EditEducationForm";
import {EditAwardForm} from "@/components/EditAwardForm";
import {EditProfessionForm} from "@/components/EditProfessionForm";
import {Link, useNavigate} from "react-router-dom";
import {EditSocialMediaForm} from "@/components/EditSocialMediaForm";
import {EditMedicalConditionForm} from "@/components/EditMedicalConditionForm";
import Cookies from "universal-cookie";
import {MembershipCardModal} from "@/components/MembershipCardModal";

export const PersonalProfile = () => {
	const {account, setAccount, loading} = React.useContext(AccountContext);

	const cookies = new Cookies();
	const locale = cookies.get("locale");

	let navigate = useNavigate();

	const updatePfpInputRef = useRef();
	const [updatedPfp, setUpdatedPfp] = useState(undefined);
	const [updatedPfpTempUrl, setUpdatedPfpTempUrl] = useState("");
	const [showUpdatePfpConfirmationDialog, setShowUpdatePfpConfirmationDialog] = useState(false);
	const [updatePfpButtonDisabled, setUpdatePfpButtonDisabled] = useState(false);

	const [showEditPersonalProfileForm, setShowEditPersonalProfileForm] = useState(false);

	const [showMembershipCardModal, setShowMembershipCardModal] = useState(false);

	const [showAddProfessionForm, setShowAddProfessionForm] = useState(false);
	const [selectedProfessionData, setSelectedProfessionData] = useState(null);
	const [showEditProfessionForm, setShowEditProfessionForm] = useState(false);

	const [showAddEducationForm, setShowAddEducationForm] = useState(false);
	const [selectedEducationData, setSelectedEducationData] = useState(null);
	const [showEditEducationForm, setShowEditEducationForm] = useState(false);

	const [showAddAwardForm, setShowAddAwardForm] = useState(false);
	const [selectedAwardData, setSelectedAwardData] = useState(null);
	const [showEditAwardForm, setShowEditAwardForm] = useState(false);

	const [showAddSocialMediaForm, setShowAddSocialMediaForm] = useState(false);
	const [selectedSocialMediaData, setSelectedSocialMediaData] = useState(null);
	const [showEditSocialMediaForm, setShowEditSocialMediaForm] = useState(false);

	const [showAddMedicalConditionForm, setShowAddMedicalConditionForm] = useState(false);
	const [selectedMedicalConditionData, setSelectedMedicalConditionData] = useState(false);
	const [showEditMedicalConditionForm, setShowEditMedicalConditionForm] = useState(false);

	function has24HoursPassed(createdAt) {
		const createdAtDate = new Date(createdAt);
		const now = new Date();

		const timeDiff = now - createdAtDate;
		const hoursDiff = timeDiff / (1000 * 60 * 60);

		return hoursDiff >= 24;
	}

	function formatStartAndEndDate(dateString) {
		// console.log("Date String: ", dateString);
		const dateObject = new Date(dateString);
		const monthName = dateObject.toLocaleString('en-US', {month: 'short'});
		const year = dateObject.getFullYear();
		// console.log("Formatted Date: ", `${monthName} ${year}`);
		return `${monthName} ${year}`;
	}

	function formatBirthDate(dateString) {
		// Parse the ISO date string into a Date object
		const date = new Date(dateString);

		// Get the day of the month
		const day = date.getUTCDate();

		// Add the appropriate ordinal suffix to the day
		const dayWithSuffix = day + (day % 10 === 1 && day !== 11 ? 'st' :
			day % 10 === 2 && day !== 12 ? 'nd' :
				day % 10 === 3 && day !== 13 ? 'rd' : 'th');

		// Get the month as a short string
		const month = date.toLocaleString('en-US', {month: 'short'});

		// Get the year
		const year = date.getUTCFullYear();

		// Format the final output
		return `${dayWithSuffix} ${month}, ${year}`;
	}

	const updatePfp = async (file) => {
		const formData = new FormData();
		formData.append('user-pfp', file);
		formData.append('accountId', account._id);

		const updatePfpResponse = await updateProfilePicture(formData);

		console.log("Update PFP Response: ", updatePfpResponse);

		if (updatePfpResponse?.success) {
			setAccount(updatePfpResponse.data);
			setUpdatePfpButtonDisabled(false);
			setShowUpdatePfpConfirmationDialog(false);
		}
	}

	const maritalStatusLabel = (maritalStatus) => {
		switch (maritalStatus) {
			case 'Single':
				return t("PROFILE_PAGE.MARITAL_STATUS.SINGLE");
			case 'Married':
				return t("PROFILE_PAGE.MARITAL_STATUS.MARRIED");
			case 'Widowed':
				return account.gender === "Male"
					? t("PROFILE_PAGE.MARITAL_STATUS.WIDOWER")
					: t("PROFILE_PAGE.MARITAL_STATUS.WIDOWED");
			case 'Divorced':
				return t("PROFILE_PAGE.MARITAL_STATUS.DIVORCED");
			case 'Minor':
				return t("PROFILE_PAGE.MARITAL_STATUS.MINOR");
			default:
				return t("PROFILE_PAGE.MARITAL_STATUS.SINGLE");
		}
	}

	const handleSocialMediaIcon = (socialMediaName) => {
		switch (socialMediaName) {
			case 'whatsapp-number':
				return "/assets/whatsapp-icon.svg";
			case 'facebook':
				return "/assets/facebook-icon.svg";
			case 'twitter':
				return "/assets/twitter-x-icon.svg";
			case 'linkedin':
				return "/assets/linkedin-icon.svg";
			case 'pinterest':
				return "/assets/pinterest-icon.svg";
			case 'youtube':
				return "/assets/youtube-icon.svg";
			case 'instagram':
				return "/assets/instagram-icon.svg";
		}
	}

	const handleMedicalConditionIcon = (medicalConditionName) => {
		switch (medicalConditionName) {
			case 'Allergies':
				return "/assets/allergies-icon-light.svg";
			case 'Asthma':
				return "/assets/asthma-icon-light.svg";
			case 'Back Problems':
				return "/assets/back-problems-icon-light.svg";
			case 'Nerve Damage':
				return "/assets/nerve-damage-icon-light.svg";
			case 'Diabetes':
				return "/assets/diabetes-icon-light.svg";
			case 'Cancer':
				return "/assets/cancer-icon-light.svg";
			case 'BP':
				return "/assets/bp-icon-light.svg";
			case 'Epilepsy':
				return "/assets/epilepsy-icon-light.svg";
			case 'Other':
				return "/assets/medical-condition-default-icon-light.svg";
		}
	}

	if (!account || loading) {
		return (
			<div className={"w-[100vw] h-[100vh] bg-[#F7F7F7] flex items-center justify-center"}>
				<Header/>
				<h1 className={"text-[5vw] font-custom-mono-bold"}>
					Loading...
				</h1>
			</div>
		)
	}

	return (
		<>
			<MembershipCardModal
				showMembershipCardModal={showMembershipCardModal}
				setShowMembershipCardModal={setShowMembershipCardModal}
				accountDetails={account}
			/>

			<AddProfessionForm
				showAddProfessionForm={showAddProfessionForm}
				setShowAddProfessionForm={setShowAddProfessionForm}
				accountDetails={account}
				setAccountDetails={setAccount}
			/>

			<EditProfessionForm
				showEditProfessionForm={showEditProfessionForm}
				setShowEditProfessionForm={setShowEditProfessionForm}
				professionData={selectedProfessionData}
				setProfessionData={setSelectedProfessionData}
				accountDetails={account}
				setAccountDetails={setAccount}
			/>

			<AddEducationForm
				showAddEducationForm={showAddEducationForm}
				setShowAddEducationForm={setShowAddEducationForm}
				accountDetails={account}
				setAccountDetails={setAccount}
			/>

			<EditEducationForm
				showEditEducationForm={showEditEducationForm}
				setShowEditEducationForm={setShowEditEducationForm}
				educationData={selectedEducationData}
				setEducationData={setSelectedEducationData}
				accountDetails={account}
				setAccountDetails={setAccount}
			/>

			<AddAwardForm
				showAddAwardForm={showAddAwardForm}
				setShowAddAwardForm={setShowAddAwardForm}
				accountDetails={account}
				setAccountDetails={setAccount}
			/>

			<EditAwardForm
				showEditAwardForm={showEditAwardForm}
				setShowEditAwardForm={setShowEditAwardForm}
				awardData={selectedAwardData}
				setAwardData={setSelectedAwardData}
				accountDetails={account}
				setAccountDetails={setAccount}
			/>

			<AddSocialMediaForm
				showAddSocialMediaForm={showAddSocialMediaForm}
				setShowAddSocialMediaForm={setShowAddSocialMediaForm}
				accountDetails={account}
				setAccountDetails={setAccount}
			/>

			<EditSocialMediaForm
				showEditSocialMediaForm={showEditSocialMediaForm}
				setShowEditSocialMediaForm={setShowEditSocialMediaForm}
				socialMediaData={selectedSocialMediaData}
				setSocialMediaData={setSelectedSocialMediaData}
				accountDetails={account}
				setAccountDetails={setAccount}
			/>

			<AddMedicalConditionForm
				showAddMedicalConditionForm={showAddMedicalConditionForm}
				setShowAddMedicalConditionForm={setShowAddMedicalConditionForm}
				accountDetails={account}
				setAccountDetails={setAccount}
			/>

			<EditMedicalConditionForm
				showEditMedicalConditionForm={showEditMedicalConditionForm}
				setShowEditMedicalConditionForm={setShowEditMedicalConditionForm}
				medicalConditionData={selectedMedicalConditionData}
				setMedicalConditionData={setSelectedMedicalConditionData}
				accountDetails={account}
				setAccountDetails={setAccount}
			/>

			{
				// has24HoursPassed(account.createdAt)
				//     ? (
				//         account.familyTreeAdminId === account._id && (
				!loading && account &&
				<EditProfileForm
					showEditPersonalProfileForm={showEditPersonalProfileForm}
					setShowEditPersonalProfileForm={setShowEditPersonalProfileForm}
					accountDetails={account}
					setAccountDetails={setAccount}
				/>
				//     )
				// ) : (
				//     !loading &&
				//     <EditProfileForm
				//         showEditPersonalProfileForm={showEditPersonalProfileForm}
				//         setShowEditPersonalProfileForm={setShowEditPersonalProfileForm}
				//         accountDetails={account}
				//         setAccountDetails={setAccount}
				//     />
				// )
			}

			<div
				className={`w-[100vw] landscape:h-[100dvh] portrait:h-fit 
                    bg-[#F7F7F7] flex items-center justify-center`}
			>
				<Header/>

				<div
					className="landscape:fixed
                        portrait:overflow-y-scroll portrait:h-[100vh] thin-scrollbar
                        portrait:pt-[4rem]
                        portrait:sm:pt-[5.5rem]
                        portrait:jk:pt-[5rem]

                        landscape:pt-[3.5rem]
                        landscape:jk:pt-[4.5rem]

                        portrait:pb-[0.75rem] landscape:pb-[0.5rem]
                        pl-[0.75rem] pr-[0.4rem] gap-[0.75rem]

                        auto-rows-fr grid portrait:auto-rows-max
                        portrait:grid-cols-1

                        portrait:sm:grid-rows-none
                        portrait:sm:grid-cols-5 landscape:grid-cols-11
                        min-h-full max-h-full min-w-full max-w-full"
				>
					{/* PFP | Basic Details | Contact Details */}
					<div
						className="portrait:min-h-[80vh] portrait:sm:h-auto portrait:sm:col-span-2
                            landscape:col-span-2 portrait:row-span-1 grid grid-cols-1"
					>
						<div
							className="portrait:p-[1.25rem] portrait:jk:p-[1.75rem] portrait:lg:p-[2.25rem]
                                landscape:p-[0.75rem] landscape:jk:p-[0.9rem] landscape:lg:p-[1.15rem]
                                gap-[0.5rem] relative rounded-lg bg-white shadow-xl
                                flex flex-col overflow-y-scroll thin-scrollbar"
						>
							{/* Back Button */}
							<button
								onClick={() => navigate(-1)}
								className={`absolute aspect-square
                                    portrait:top-[1.25rem] portrait:jk:top-[1.75rem] portrait:lg:top-[2.25rem]
                                    portrait:left-[1.25rem] portrait:jk:left-[1.75rem] portrait:lg:left-[2.25rem]
                                    
                                    portrait:h-[1.75rem] portrait:jk:h-[2.15rem] portrait:lg:h-[2.5rem]
                                    
                                    landscape:h-[0.75rem] landscape:sm:h-[0.95rem]
                                    landscape:jk:h-[1.25rem] landscape:lg:h-[1.75rem]
                                    
                                    flex items-center justify-center bg-[#D4D4D4] rounded-md shadow-lg 
                                    hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4] 
                                    transition duration-300 ease-out`}
							>
								<img
									src={"/assets/square-back-icon.svg"}
									className={"w-[75%] aspect-square"}
									alt={"Back"}
									title={"Back"}
								/>
							</button>

							{/* Edit Button */}
							{
								// has24HoursPassed(account.createdAt)
								//     ? account.familyTreeAdminId === account._id && (
								//     <button
								//         onClick={() => setShowEditPersonalProfileForm(true)}
								//         className={`absolute top-[2vh] right-[2vh] h-[1.75vw] w-[1.75vw]
								//                     flex items-center justify-center bg-[#D4D4D4] rounded-md shadow-lg
								//                     hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4]
								//                     transition duration-300 ease-out`}
								//     >
								//         <img
								//             src={"/assets/pencil-edit-icon.png"}
								//             className={"w-[75%] aspect-square"}
								//             alt={"Edit Basic Details"}
								//             title={"Edit Basic Details"}
								//         />
								//     </button>
								// )
								//     : (
								<button
									onClick={() => setShowEditPersonalProfileForm(true)}
									className={`absolute aspect-square
                                        portrait:top-[1.25rem] portrait:jk:top-[1.75rem] portrait:lg:top-[2.25rem]
                                        portrait:right-[1.25rem] portrait:jk:right-[1.75rem] portrait:lg:right-[2.25rem]
                                        
                                        landscape:right-[0.75rem] landscape:jk:right-[0.9rem] landscape:lg:right-[1.15rem]
                                        
                                        portrait:h-[1.75rem] portrait:jk:h-[2.15rem] portrait:lg:h-[2.5rem]
                                        
                                        landscape:h-[0.75rem] landscape:sm:h-[0.95rem]
                                        landscape:jk:h-[1.25rem] landscape:lg:h-[1.75rem]
                                        
                                        flex items-center justify-center bg-[#D4D4D4] rounded-md shadow-lg 
                                        hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4] 
                                        transition duration-300 ease-out`}
								>
									<img
										src={"/assets/pencil-edit-icon.png"}
										className={"w-[75%] aspect-square"}
										alt={"Edit Basic Details"}
										title={"Edit Basic Details"}
									/>
								</button>
								// )
							}

							{/* PFP | Gender | Name | Family Tree Name */}
							<div
								className={`overflow-x-clip gap-y-2 flex 
                                    flex-col items-center justify-between`}
							>
								{/* PFP */}
								<div
									className={`relative flex items-center justify-center 
                                        shadow-xl rounded-full z-10 aspect-square
                                        portrait:h-[10rem] portrait:jk:h-[12.5rem] portrait:lg:h-[15rem]
                                        landscape:md:h-[5rem] landscape:jk:h-[6.5rem] landscape:lg:h-[10rem]
                                        ${account.imageUrlBg} border-4 border-white`}
								>
									{
										account.imageUrl &&
										account.imageUrl.length <= 2
											? (
												<p
													className="text-white font-custom-mono-bold
                                                        portrait:text-[3rem] portrait:jk:text-[3.75rem] portrait:lg:text-[4.5rem]
                                                        landscape:text-[1.75rem] landscape:jk:text-[2.25rem] landscape:lg:text-[3rem]"
												>
													{account.imageUrl.toUpperCase()}
												</p>

											) : (
												<img
													className="object-cover drop-shadow-2xl h-full w-full rounded-full"
													src={account.imageUrl}
													alt="User Profile Image"
													referrerPolicy={"no-referrer"}
												/>
											)
									}

									{/* Edit PFP Button */}
									{
										// has24HoursPassed(account.createdAt)
										//     ? account.familyTreeAdminId === account._id && (
										//     <form
										//         className={`absolute aspect-square bottom-[5%] right-[5%]
										//             portrait:h-[1.75rem] portrait:jk:h-[2.15rem] portrait:lg:h-[2.5rem]
										//
										//             landscape:h-[0.75rem] landscape:sm:h-[0.95rem]
										//             landscape:jk:h-[1.5rem] landscape:lg:h-[1.75rem]
										//
										//             flex items-center justify-center
										//
										//             cursor-pointer bg-white shadow-2xl shadow-pink-600
										//             rounded-lg rounded-tr-sm`}
										//         onClick={() => updatePfpInputRef.current.click()}
										//     >
										//         <input
										//             ref={updatePfpInputRef}
										//             hidden
										//             type={"file"}
										//             accept={"image/*"}
										//             className={"edit-pfp"}
										//             onFocus={(event) => console.log("File Input Focused Event: ", event)}
										//             onChange={({target: {files}}) => {
										//                 console.log("Image File: ", files);
										//                 if (files.length > 0) {
										//                     setUpdatedPfp(files[0]);
										//                     setUpdatedPfpTempUrl(URL.createObjectURL(files[0]));
										//                     setShowUpdatePfpConfirmationDialog(true);
										//                 }
										//             }}
										//         />
										//         <img
										//             className="drop-shadow-2xl h-[75%] aspect-square"
										//             src={'assets/edit-pfp.png'}
										//             alt="User Profile Image"
										//         />
										//     </form>
										// )
										//     : (
										<form
											className={`absolute aspect-square bottom-[5%] right-[5%] 
                                                portrait:h-[1.75rem] portrait:jk:h-[2.15rem] portrait:lg:h-[2.5rem]
                                            
                                                landscape:h-[0.75rem] landscape:sm:h-[0.95rem]
                                                landscape:jk:h-[1.25rem] landscape:lg:h-[1.75rem]
                                                        
                                                flex items-center justify-center
                                                        
                                                cursor-pointer bg-white shadow-2xl shadow-pink-600 
                                                rounded-lg rounded-tr-sm`}
											onClick={() => updatePfpInputRef.current.click()}
										>
											<input
												ref={updatePfpInputRef}
												hidden
												type={"file"}
												accept={"image/*"}
												className={"edit-pfp"}
												onFocus={(event) => console.log("File Input Focused Event: ", event)}
												onChange={({target: {files}}) => {
													console.log("Image File: ", files);
													if (files.length > 0) {
														setUpdatedPfp(files[0]);
														setUpdatedPfpTempUrl(URL.createObjectURL(files[0]));
														setShowUpdatePfpConfirmationDialog(true);
													}
												}}
											/>
											<img
												className="drop-shadow-2xl h-[75%] aspect-square"
												src={'assets/edit-pfp.png'}
												alt="User Profile Image"
											/>
										</form>
										// )
									}
								</div>

								{/* Basic Details */}
								<div className={"w-full flex flex-col items-center justify-between gap-1"}>
									{/* Gender | Name */}
									<div className={"flex items-center justify-center mt-[1vh] gap-2"}>
										<div className={"w-full h-full flex-1 flex items-center justify-end"}>
											<img
												className="aspect-square
                                                    portrait:h-[1.75rem] portrait:jk:h-[2.15rem] portrait:lg:h-[2.5rem]
                                                    landscape:h-[0.75rem] landscape:jk:h-[0.95rem] landscape:lg:h-[1.25rem]"
												src={
													account.gender === "Male"
														? "/assets/male-gender-icon-bold.png"
														: "/assets/female-gender-icon-bold.png"
												}
												alt={account.gender === "Male" ? "Male" : "Female"}
											/>
										</div>
										<p
											className="flex-1 text-center font-custom-bold leading-tight
                                                portrait:text-[1.5rem] portrait:jk:text-[1.75rem] portrait:lg:text-[2rem]
                                                landscape:text-[0.75rem] landscape:jk:text-[1rem] landscape:lg:text-[1.25rem]"
										>
											{account.firstName + ' ' + account.middleName}
										</p>
										<div className={"w-full h-full flex-1 flex items-center justify-start"}>
											<div
												className="aspect-square bg-transparent
                                                    portrait:h-[1.75rem] portrait:jk:h-[2.15rem] portrait:lg:h-[2.5rem]
                                                    landscape:h-[0.75rem] landscape:jk:h-[0.95rem] landscape:lg:h-[1.25rem]"
											/>
										</div>
									</div>

									{/* Family Tree Name */}
									<p
										className="w-full text-center text-[#FF6600] font-custom-semi-bold
                                            portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                            landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
									>
										{
											'(' +
											(locale === "en"
												? account.familyTreeName
												: account.familyTreeNameDevanagariScript) + ')'
										}
									</p>

									{/* Membership Number */}
									{
										account.membershipNumber && (
											<div className={"w-full flex-1 flex items-center justify-center gap-1"}>
												<div
													className={`portrait:h-[1rem] portrait:jk:h-[1.25rem] portrait:lg:h-[1.5rem]
                                                            landscape:h-[0.75rem] landscape:jk:h-[0.95rem] landscape:lg:h-[1.25rem]
                                                            aspect-square flex items-center justify-end`}
												>
													<img
														className="h-full aspect-square"
														src={"/assets/membership-icon-filled-gray.svg"}
														alt={"Membership"}
													/>
												</div>
												<p
													className="leading-tight text-[#292D32] text-center font-custom-bold
                                                        portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                        landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
												>
													{t("PROFILE_PAGE.MEMBERSHIP_NUMBER")}
												</p>
												<p
													className="line-clamp-1 leading-tight text-[#6D6D6D] text-center font-custom-bold
                                                        portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                        landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
												>
													{`${account.membershipType}-${account.membershipNumber}`}
												</p>
												<button
													className={`portrait:h-[1rem] portrait:jk:h-[1.25rem] portrait:lg:h-[1.5rem]
                                                        landscape:h-[0.75rem] landscape:jk:h-[0.95rem] landscape:lg:h-[1.25rem]
                                                        aspect-square flex items-center justify-end`}
													onClick={() => setShowMembershipCardModal(true)}
												>
													<img
														className="h-full aspect-square"
														src={"/assets/view-membership-card-icon.svg"}
														alt={"Membership"}
													/>
												</button>
											</div>
										)
									}
								</div>

								{/* Divider - Header */}
								<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full mt-[1.5vh]"}/>
							</div>

							{/* Basic Details */}
							<div
								className={`gap-1 flex flex-col items-start`}
							>
								{/* Date Of Birth */}
								<div className={"w-full flex items-start gap-1"}>
									<p
										className="w-1/3 text-[#9B9B9B] font-custom-regular
                                            portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                            landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
									>
										{t("PROFILE_PAGE.DOB")}
									</p>
									<p
										className="flex-1 text-[#6D6D6D] font-custom-medium
                                            portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                            landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
									>
										{formatBirthDate(account.dateOfBirth)}
									</p>
								</div>

								{/* Blood Group */}
								<div className={"w-full flex items-start gap-1"}>
									<div className={"w-1/3 flex gap-1"}>
										<img
											src={"/assets/blood-drop-icon.png"}
											className="aspect-square
                                                portrait:h-[1rem] portrait:jk:h-[1.25rem] portrait:lg:h-[1.5rem]
                                                landscape:h-[0.75rem] landscape:jk:h-[0.95rem] landscape:lg:h-[1.25rem]"
											alt={"Blood Group"}
											title={"Blood Group"}
										/>
										<p
											className="text-[#9B9B9B] font-custom-regular
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
										>
											{t("PROFILE_PAGE.BLOOD_GROUP")}
										</p>
									</div>
									<p
										className="flex-1 text-[#6D6D6D] font-custom-medium
                                            portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                            landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
									>
										{account.bloodGroup}
									</p>
								</div>

								{/* Marital Status */}
								<div className={"w-full flex items-start gap-1"}>
									<div className={"w-1/3 flex gap-1"}>
										<img
											src={"/assets/marriage-icon.png"}
											className="aspect-square
                                                portrait:h-[1rem] portrait:jk:h-[1.25rem] portrait:lg:h-[1.5rem]
                                                landscape:h-[0.75rem] landscape:jk:h-[0.95rem] landscape:lg:h-[1.25rem]"
											alt={"Marriage"}
											title={"Marriage"}
										/>
										<p
											className="text-[#9B9B9B] font-custom-regular
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
										>
											{t("PROFILE_PAGE.MARITAL_STATUS.LABEL")}
										</p>
									</div>
									<p
										className="flex-1 text-[#6D6D6D] font-custom-medium
                                            portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                            landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
									>
										{maritalStatusLabel(account.maritalStatus)}
									</p>
								</div>

								{/* Biography */}
								<div className={"w-full flex items-start gap-1"}>
									<p
										className="w-1/3 text-[#9B9B9B] font-custom-regular
                                            portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                            landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
									>
										{t("PROFILE_PAGE.BIOGRAPHY")}
									</p>
									<p
										className={`overflow-scroll thin-scrollbar flex-1 
                                            line-clamp-4 font-custom-medium leading-tight
                                            ${account.biography === "" ? 'text-transparent' : 'text-[#6D6D6D]'} 
                                            portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                            landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]`}
									>
										{account.biography === "" ? 'Biography' : account.biography}
									</p>
								</div>

								{/* Side of Family | Name Before / After Marriage */}
								{
									account.gender === "Female" && (
										<>
											{/* Side of Family */}
											<div className={"w-full flex items-start gap-1"}>
												<p
													className="w-1/3 text-[#9B9B9B] font-custom-regular
                                                        portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                        landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
												>
													{t("PROFILE_PAGE.SIDE_OF_FAMILY.LABEL")}
												</p>
												<p
													className="flex-1 text-[#6D6D6D] font-custom-medium
                                                        portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                        landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
												>
													{account.sideOfFamily}
												</p>
											</div>

											{/* Name Before / After Marriage */}
											{
												((account.sideOfFamily === "MaherVashin" &&
														(account.firstNameAfterMarriage ||
															account.spouseNameAfterMarriage ||
															account.lastNameAfterMarriage)) ||
													(account.sideOfFamily === "SasurVashin" &&
														(account.firstNameBeforeMarriage ||
															account.fathersNameBeforeMarriage ||
															account.lastNameBeforeMarriage))) && (
													<div className={"w-full flex items-start gap-1"}>
														<div className={"w-1/3"}>
															<p
																className="text-[#9B9B9B] font-custom-regular
                                                                        portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                                        landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
															>
																{t("PROFILE_PAGE.SIDE_OF_FAMILY.NAME")}
															</p>
															<div className={"flex flex-row"}>
																<p
																	className="text-[#9B9B9B] font-custom-regular
                                                                        portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                                        landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
																>
																	(
																	{account.sideOfFamily === "SasurVashin"
																		? t("PROFILE_PAGE.SIDE_OF_FAMILY.BEFORE")
																		: t("PROFILE_PAGE.SIDE_OF_FAMILY.AFTER")}
																</p>
																<img
																	src={"/assets/marriage-icon.png"}
																	className="aspect-square
                                                                        portrait:h-[1rem] portrait:jk:h-[1.25rem] portrait:lg:h-[1.5rem]
                                                                        landscape:h-[0.75rem] landscape:jk:h-[0.95rem] landscape:lg:h-[1.25rem]"
																	alt={"Marriage"}
																	title={"Marriage"}
																/>
																<p
																	className="text-[#9B9B9B] font-custom-regular
                                                                        portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                                        landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
																>
																	)
																</p>
															</div>
														</div>
														<div className={"flex items-center flex-1 h-full"}>
															<p
																className={`overflow-scroll thin-scrollbar flex-1 
                                                                    text-[#6D6D6D] font-custom-medium line-clamp-2 
                                                                    text-ellipsis leading-tight
                                                                    portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                                    landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]`}
															>
																{
																	account.sideOfFamily === "MaherVashin"
																		?
																		account.firstNameAfterMarriage + " "
																		+ account.spouseNameAfterMarriage + " "
																		+ account.lastNameAfterMarriage
																		:
																		account.firstNameBeforeMarriage + " "
																		+ account.fathersNameBeforeMarriage + " "
																		+ account.lastNameBeforeMarriage
																}
															</p>
														</div>
													</div>
												)
											}
										</>
									)
								}
							</div>

							{/* Contact Details */}
							<div className={"flex-1"}>
								{/* Divider - Basic Detail / Contact Details */}
								<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full mb-[1.5vh]"}/>

								{/* Contact Details - Header */}
								<div className={"w-full flex items-center justify-between"}>
									<p className={"text-[#3D3D3D] text-[2.25vh] font-custom-bold"}>
										{t("PROFILE_PAGE.CONTACT_DETAILS")}
									</p>
								</div>

								{/* Divider - Header */}
								<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full mt-[1.5vh]"}/>

								{/* Contact Details - Content */}
								<div
									className={`w-full py-[1vh] flex-1 flex flex-col items-start gap-0.5`}
								>
									{/* Email ID */}
									<div className={"w-full flex items-start gap-1"}>
										<p
											className="w-[40%] text-[#9B9B9B] font-custom-regular
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
										>
											{t("PROFILE_PAGE.EMAIL")}
										</p>
										<p
											className={`flex-1 font-custom-medium 
                                                overflow-scroll thin-scrollbar leading-tight
                                                ${account.email ? 'text-[#6D6D6D]' : 'text-transparent'}
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]`}
											style={{userSelect: account.email ? "auto" : "none"}}
										>
											{account.email ? account.email : 'Email ID'}
										</p>
									</div>

									{/* Phone Number */}
									<div className={"w-full flex items-start gap-1"}>
										<p
											className="w-[40%] text-[#9B9B9B] font-custom-regular
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
										>
											{t("PROFILE_PAGE.PHONE")}
										</p>
										<p
											className={`flex-1 text-[1.5vh] font-custom-medium 
                                                ${account.phone?.number ? 'text-[#6D6D6D]' : 'text-transparent'}
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]`}
											style={{userSelect: account.phone?.number ? "auto" : "none"}}
										>
											{
												account.phone?.number
													? (account.phone.dialingCode + ' ' + account.phone.number)
													: '+91 1234567890'
											}
										</p>
									</div>

									{/* Home Phone Number */}
									<div className={"w-full flex items-start gap-1"}>
										<p
											className="w-[40%] text-[#9B9B9B] font-custom-regular
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
										>
											{t("PROFILE_PAGE.HOME_PHONE")}
										</p>

										<p
											className={`flex-1 font-custom-medium 
                                                ${account.homePhone?.number ? 'text-[#6D6D6D]' : 'text-transparent'}
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]`}
											style={{userSelect: account.homePhone?.number ? "auto" : "none"}}
										>
											{
												account.homePhone?.number
													? (account.homePhone.dialingCode + ' ' + account.homePhone.number)
													: '+91 1234567890'
											}
										</p>
									</div>

									{/* Office Phone Number */}
									<div className={"w-full flex items-start gap-1"}>
										<p
											className="w-[40%] text-[#9B9B9B] font-custom-regular
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
										>
											{t("PROFILE_PAGE.OFFICE_PHONE")}
										</p>
										<p
											className={`flex-1 font-custom-medium 
                                                ${account.officePhone?.number ? 'text-[#6D6D6D]' : 'text-transparent'}
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]`}
											style={{userSelect: account.officePhone?.number ? "auto" : "none"}}
										>
											{
												account.officePhone?.number
													? (account.officePhone.dialingCode + ' ' + account.officePhone.number)
													: '+91 1234567890'
											}
										</p>
									</div>

									{/* Address */}
									<div className={"w-full flex items-start gap-1"}>
										<p
											className="w-[40%] text-[#9B9B9B] font-custom-regular
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]"
										>
											{t("PROFILE_PAGE.ADDRESS")}
										</p>
										<p
											className={`flex-1 font-custom-medium 
                                                leading-tight overflow-scroll thin-scrollbar
                                                ${account.address?.addressLine1 ? 'text-[#6D6D6D]' : 'text-transparent'}
                                                portrait:text-[0.85rem] portrait:jk:text-[1rem] portrait:lg:text-[1.25rem]
                                                landscape:text-[0.5rem] landscape:jk:text-[0.65rem] landscape:lg:text-[0.8rem]`}
											style={{userSelect: account.address?.addressLine1 ? "auto" : "none"}}
										>
											{
												account.address?.addressLine1
													? `${account.address?.addressLine1}, ${account.address?.city}, ${account.address?.state}, ${account.address?.country} - ${account.address?.pincode}`
													: 'Address'
											}
										</p>
									</div>
								</div>

								{/* Divider - Footer */}
								<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full"}/>
							</div>
						</div>
					</div>

					{/* Profession | Education */}
					<div
						className="portrait:min-h-[80vh] portrait:sm:h-auto portrait:sm:col-span-3
                            landscape:col-span-3 portrait:row-span-1 grid grid-cols-1 grid-rows-2 gap-[0.75rem]"
					>
						{/* Profession */}
						<div
							className={`row-span-1 flex flex-col items-start
                                portrait:px-[1.25rem] portrait:py-[1rem]
                                portrait:jk:px-[1.75rem] portrait:jk:py-[1.5rem]
                                portrait:lg:p-[2.25rem] portrait:lg:py-[2rem]
                                
                                landscape:px-[0.75rem] landscape:py-[0.5rem]
                                landscape:jk:px-[0.9rem] landscape:jk:py-[0.6rem]
                                landscape:lg:px-[1.15rem] landscape:lg:py-[0.75rem]
                                
                                rounded-lg bg-white shadow-xl`}
						>
							{/* Profession - Header */}
							<div className={"w-full flex items-center justify-between"}>
								<p
									className="text-[#3D3D3D] font-custom-bold
                                        portrait:text-[1.25rem] portrait:md:text-[1.375rem]
                                        portrait:jk:text-[1.5rem] portrait:lg:text-[1.75rem]
                                        landscape:text-[0.85rem]
                                        landscape:jk:text-[1rem] landscape:lg:text-[1.225rem]"
								>
									{t("PROFILE_PAGE.PROFESSION")}
								</p>

								<button
									onClick={() => setShowAddProfessionForm(true)}
									className={`flex items-center justify-center aspect-square
                                        portrait:w-[1.5rem] portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]
                                        
                                        landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                        landscape:jk:w-[1.25rem] landscape:lg:w-[1.5rem]
                                        
                                        bg-[#D4D4D4] rounded-md shadow-lg 
                                        hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4] 
                                        transition duration-200 ease-out`}
								>
									<img
										src={"/assets/add-icon.png"}
										className={"w-[75%] aspect-square"}
										alt={"Add Profession"}
										title={"Add Profession"}
									/>
								</button>
							</div>

							{/* Divider - Header */}
							<div
								className="h-[0.2vh] w-full bg-[#ECECEC] rounded-full
                                    portrait:mt-[0.5rem] portrait:jk:mt-[1.5rem] portrait:lg:mt-[2rem]
                                    landscape:mt-[0.2rem] landscape:jk:mt-[0.35rem] landscape:lg:mt-[0.5rem]"
							/>

							{/* Profession List */}
							<div
								className={`w-full flex-1 flex flex-col items-start 
                                    gap-2 overflow-y-scroll thin-scrollbar
                                    portrait:py-[0.75rem] portrait:jk:py-[1.5rem] portrait:lg:py-[2rem]
                                    landscape:py-[0.75rem] landscape:jk:py-[0.9rem] landscape:lg:py-[0.75rem]`}
							>
								{
									(!account.professionDetails || account.professionDetails?.length === 0) &&
									<div className={"h-full w-full flex items-center justify-center"}>
										<img
											src={"/assets/ganeshji-logo.png"}
											className={"w-[7.5vw] aspect-square"}
											alt={"GaneshJi"}
											title={"GaneshJi"}
										/>
									</div>
								}
								{
									account.professionDetails?.map((profession, index) => (
										<div
											key={index}
											className={`h-fit w-full flex items-start 
                                                portrait:gap-[1.75vw] landscape:gap-[0.75vw]
                                                
                                                portrait:p-[0.5rem] landscape:p-[0.75rem]
                                                portrait:jk:p-[1rem] landscape:jk:p-[0.9rem]
                                                portrait:lg:p-[1.5rem] landscape:lg:p-[1.15rem]
                                                
                                                rounded-xl border border-[#ECECEC]`}
										>
											{/* Icon */}
											<div
												className={`flex items-center justify-center aspect-square 
                                                portrait:h-[3.5rem] landscape:h-[4vw]
                                                bg-[#F5F5F5] rounded-lg`}
											>
												<img
													src={"/assets/profession-icon.png"}
													className={"w-[80%] aspect-square"}
													alt={"Profession"}
													title={"Profession"}
												/>
											</div>

											{/* Details */}
											<div className={"w-full flex-1 flex flex-col items-start gap-1.5"}>
												<div className={"flex flex-col gap-0.5"}>
													<p className={"text-[#838383] text-[1.25vh] leading-tight font-custom-regular"}>
														{profession.employmentType}
													</p>
													<p className={"text-[#3D3D3D] text-[1.75vh] leading-none font-custom-bold"}>
														{profession.employmentTitle}
													</p>
												</div>
												<div className={"w-full flex flex-col gap-1"}>
													<p className={"text-[#3D3D3D] text-[1.5vh] leading-none font-custom-regular"}>
														{profession.businessOrCompanyName}
													</p>
													<p className={"text-[#3D3D3D] text-[1.5vh] leading-none font-custom-regular"}>
														{
															formatStartAndEndDate(profession.startDate)
															+ ' - '
															+ (profession.isCurrentProfession ? 'Present' : formatStartAndEndDate(profession.endDate))
														}
													</p>
												</div>
												<p className={"text-[#838383] text-[1.25vh] leading-none font-custom-regular"}>
													{`${profession.city}, ${profession.state}, ${profession.country}`}
												</p>
											</div>

											{/* Edit Button */}
											<div
												className="portrait:w-[1.5rem] portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]

                                                    landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                                    landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]

                                                    h-full flex flex-col items-center gap-2"
											>
												<button
													onClick={() => {
														setSelectedProfessionData(profession);
														setShowEditProfessionForm(true);
													}}
													className="w-full aspect-square
                                                        flex items-center justify-center
                                                        bg-[#F5F5F5] rounded-md
                                                        hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4]
                                                        transition duration-300 ease-out"
												>
													<img
														src={"/assets/pencil-edit-icon.png"}
														className={"w-[75%] aspect-square"}
														alt={"Edit Basic Details"}
														title={"Edit Basic Details"}
													/>
												</button>
											</div>
										</div>
									))
								}
								{
									account.professionDetails?.length === 1 &&
									<div className={"w-full flex-1 flex items-center justify-center"}>
										<img
											src={"/assets/ganeshji-logo.png"}
											className={"w-[4vw] aspect-square"}
											alt={"GaneshJi"}
											title={"GaneshJi"}
										/>
									</div>
								}
							</div>
						</div>

						{/* Education */}
						<div
							className={`row-span-1 flex flex-col items-start
                                portrait:px-[1.25rem] portrait:py-[1rem]
                                portrait:jk:px-[1.75rem] portrait:jk:py-[1.5rem]
                                portrait:lg:p-[2.25rem] portrait:lg:py-[2rem]
                                
                                landscape:px-[0.75rem] landscape:py-[0.75rem]
                                landscape:jk:px-[0.9rem] landscape:jk:py-[0.9rem]
                                landscape:lg:px-[1.15rem] landscape:lg:py-[1.15rem]
                                
                                rounded-lg bg-white shadow-xl`}
						>
							{/* Education - Header */}
							<div className={"w-full flex items-center justify-between"}>
								<p className={"text-[#3D3D3D] text-[2.25vh] font-custom-bold"}>
									{t("PROFILE_PAGE.EDUCATION")}
								</p>

								<button
									onClick={() => setShowAddEducationForm(true)}
									className={`flex items-center justify-center aspect-square
                                        portrait:w-[1.5rem] portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]
                                        
                                        landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                        landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]
                                        
                                        bg-[#D4D4D4] rounded-md shadow-lg 
                                        hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4] 
                                        transition duration-200 ease-out`}
								>
									<img
										src={"/assets/add-icon.png"}
										className={"w-[75%] aspect-square"}
										alt={"Add Education"}
										title={"Add Education"}
									/>
								</button>
							</div>

							{/* Divider - Header */}
							<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full my-[1vh]"}/>

							{/* Education List */}
							<div
								className={`w-full flex-1 flex flex-col items-start 
                                    gap-2 overflow-y-scroll thin-scrollbar`}
							>
								{
									(!account.educationDetails || account.educationDetails?.length === 0) &&
									<div className={"h-full w-full flex items-center justify-center"}>
										<img
											src={"/assets/ganeshji-logo.png"}
											className={"w-[7.5vw] aspect-square"}
											alt={"GaneshJi"}
											title={"GaneshJi"}
										/>
									</div>
								}
								{
									account.educationDetails?.map((education, index) => (
										<div
											key={index}
											className={`h-fit w-full flex items-start 
                                                portrait:gap-[1.75vw] landscape:gap-[0.75vw]
                                                
                                                portrait:p-[0.5rem] landscape:p-[0.75rem]
                                                portrait:jk:p-[1rem] landscape:jk:p-[0.9rem]
                                                portrait:lg:p-[1.5rem] landscape:lg:p-[1.15rem]
                                                
                                                rounded-xl border border-[#ECECEC]`}
										>
											{/* Icon */}
											<div
												className={`flex items-center justify-center aspect-square 
                                                    portrait:h-[3.5rem] landscape:h-[4vw]
                                                    bg-[#F5F5F5] rounded-lg`}
											>
												<img
													src={"/assets/education-icon.png"}
													className={"w-[80%] aspect-square"}
													alt={"Education"}
													title={"Education"}
												/>
											</div>

											{/* Details */}
											<div className={"w-full flex-1 flex flex-col items-start gap-1.5"}>
												<p className={"text-[#3D3D3D] text-[1.75vh] leading-none font-custom-bold"}>
													{education.schoolOrCollegeName}
												</p>
												<div className={"flex flex-col gap-1"}>
													<p className={"text-[#3D3D3D] text-[1.5vh] leading-none font-custom-regular"}>
														{`${education.degree}, ${education.courseOfStudy}`}
													</p>
													<p className={"text-[#3D3D3D] text-[1.5vh] leading-none font-custom-regular"}>
														{`Result: ${education.result}`}
													</p>
												</div>
												<div className={"w-full flex flex-col gap-1"}>
													<p className={"text-[#838383] text-[1.25vh] leading-none font-custom-regular"}>
														{
															formatStartAndEndDate(education.startDate)
															+ ' - '
															+ (education.isCurrentEducation ? 'Present' : formatStartAndEndDate(education.endDate))
														}
													</p>
													<p className={"text-[#838383] text-[1.25vh] leading-none font-custom-regular"}>
														{`${education.city}, ${education.state}, ${education.country}`}
													</p>
												</div>
											</div>

											{/* Edit Button */}
											<div
												className="portrait:w-[1.5rem] portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]

                                                    landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                                    landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]

                                                    h-full flex flex-col items-center gap-2"
											>
												<button
													onClick={() => {
														setSelectedEducationData(education);
														setShowEditEducationForm(true);
													}}
													className={`w-full aspect-square
                                                        flex items-center justify-center
                                                        bg-[#F5F5F5] rounded-md
                                                        hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4]
                                                        transition duration-300 ease-out`}
												>
													<img
														src={"/assets/pencil-edit-icon.png"}
														className={"w-[75%] aspect-square"}
														alt={"Edit Basic Details"}
														title={"Edit Basic Details"}
													/>
												</button>
											</div>
										</div>
									))
								}
								{
									account.educationDetails?.length === 1 &&
									<div className={"w-full flex-1 flex items-center justify-center"}>
										<img
											src={"/assets/ganeshji-logo.png"}
											className={"w-[4vw] aspect-square"}
											alt={"GaneshJi"}
											title={"GaneshJi"}
										/>
									</div>
								}
							</div>
						</div>
					</div>

					{/* Awards */}
					<div
						className="portrait:min-h-[80vh] portrait:sm:h-auto portrait:sm:col-span-2
                            landscape:col-span-3 portrait:row-span-1 grid grid-cols-1"
					>
						<div
							className="flex flex-col items-start
                                portrait:px-[1.25rem] portrait:py-[1rem]
                                portrait:jk:px-[1.75rem] portrait:jk:py-[1.5rem]
                                portrait:lg:p-[2.25rem] portrait:lg:py-[2rem]

                                landscape:px-[0.75rem] landscape:py-[0.75rem]
                                landscape:jk:px-[0.9rem] landscape:jk:py-[0.9rem]
                                landscape:lg:px-[1.15rem] landscape:lg:py-[1.15rem]

                                rounded-lg bg-white shadow-xl"
						>
							{/* Awards - Header */}
							<div className={"w-full flex items-center justify-between"}>
								<p className={"text-[#3D3D3D] text-[2.25vh] font-custom-bold"}>
									{t("PROFILE_PAGE.AWARDS")}
								</p>

								<button
									onClick={() => setShowAddAwardForm(true)}
									className={`flex items-center justify-center aspect-square
                                        portrait:w-[1.5rem] portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]
                                        
                                        landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                        landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]
                                        
                                        bg-[#D4D4D4] rounded-md shadow-lg 
                                        hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4] 
                                        transition duration-200 ease-out`}
								>
									<img
										src={"/assets/add-icon.png"}
										className={"w-[75%] aspect-square"}
										alt={"Add Awards"}
										title={"Add Awards"}
									/>
								</button>
							</div>

							{/* Divider - Header */}
							<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full my-[1vh]"}/>

							{/* Awards List */}
							<div
								className={`w-full flex-1 flex flex-col items-start 
                                    gap-3 overflow-y-scroll thin-scrollbar`}
							>
								{
									(!account.awardDetails || account.awardDetails?.length === 0) &&
									<div className={"h-full w-full flex items-center justify-center"}>
										<img
											src={"/assets/ganeshji-logo.png"}
											className={"w-[7.5vw] aspect-square"}
											alt={"GaneshJi"}
											title={"GaneshJi"}
										/>
									</div>
								}
								{
									account.awardDetails?.map((award, index) => (
										<div
											key={index}
											className={`h-fit w-full flex items-start 
                                                portrait:gap-[1.75vw] landscape:gap-[0.75vw]
                                                
                                                portrait:p-[0.5rem] landscape:p-[0.75rem]
                                                portrait:jk:p-[1rem] landscape:jk:p-[0.9rem]
                                                portrait:lg:p-[1.5rem] landscape:lg:p-[1.15rem]
                                                
                                                rounded-xl border border-[#ECECEC]`}
										>
											{/* Icon */}
											<div
												className={`flex items-center justify-center aspect-square 
                                                    portrait:h-[3.5rem] landscape:h-[4vw]
                                                    bg-[#F5F5F5] rounded-lg`}
											>
												<img
													src={"/assets/award-icon.png"}
													className={"w-[80%] aspect-square"}
													alt={"Award"}
													title={"Award"}
												/>
											</div>

											{/*Details */}
											<div className={"w-full flex-1 flex flex-col items-start gap-1.5"}>
												<p className={"text-[#3D3D3D] text-[2vh] leading-none font-custom-bold"}>
													{award.awardTitle}
												</p>
												<p className={"line-clamp-6 overflow-y-scroll thin-scrollbar text-[#838383] text-[1.5vh] leading-tight font-custom-regular"}>
													{award.awardDescription}
												</p>
											</div>

											{/* Edit Button */}
											<div
												className="portrait:w-[1.5rem] portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]

                                                    landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                                    landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]

                                                    h-full flex flex-col items-center gap-2"
											>
												<button
													onClick={() => {
														setSelectedAwardData(award);
														setShowEditAwardForm(true);
													}}
													className="w-full aspect-square
                                                        flex items-center justify-center
                                                        bg-[#F5F5F5] rounded-md
                                                        hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4]
                                                        transition duration-300 ease-out"
												>
													<img
														src={"/assets/pencil-edit-icon.png"}
														className={"w-[75%] aspect-square"}
														alt={"Edit Basic Details"}
														title={"Edit Basic Details"}
													/>
												</button>
											</div>
										</div>
									))
								}
								{
									account.awardDetails?.length !== 0 && account.awardDetails?.length < 5 &&
									<div className={"w-full flex-1 flex items-center justify-center"}>
										<img
											src={"/assets/ganeshji-logo.png"}
											className={"w-[5vw] aspect-square"}
											alt={"GaneshJi"}
											title={"GaneshJi"}
										/>
									</div>
								}
							</div>
						</div>
					</div>

					{/* Social Media Links | Medical Conditions */}
					<div
						className="portrait:min-h-[80vh] portrait:sm:h-auto portrait:sm:col-span-3
                            landscape:col-span-3 portrait:row-span-1 grid grid-cols-1 grid-rows-4 gap-[0.75rem]"
					>
						{/* Social Media Links */}
						<div
							className={`row-span-1 flex flex-col items-start 
                                portrait:pt-[1rem] landscape:pt-[0.75rem]
                                portrait:jk:pt-[1.5rem] landscape:jk:pt-[0.9rem]
                                portrait:lg:pt-[2rem] landscape:lg:pt-[1.15rem]
                                
                                rounded-lg bg-white shadow-xl`}
						>
							{/* Social Media - Header */}
							<div
								className="w-full flex flex-col items-start
                                    portrait:px-[1.25rem] landscape:px-[0.75rem]
                                    portrait:jk:px-[1.75rem] landscape:jk:px-[0.9rem]
                                    portrait:lg:p-[2.25rem] landscape:lg:px-[1.15rem]"
							>
								<div className={"w-full flex items-center justify-between"}>
									<p className={"text-[#3D3D3D] text-[2.25vh] font-custom-bold"}>
										{t("PROFILE_PAGE.SOCIAL_MEDIA")}
									</p>

									<button
										onClick={() => setShowAddSocialMediaForm(true)}
										className={`flex items-center justify-center aspect-square
                                            portrait:w-[1.5rem] portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]
                                            
                                            landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                            landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]
                                            
                                            bg-[#D4D4D4] rounded-md shadow-lg 
                                            hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4] 
                                            transition duration-200 ease-out`}
									>
										<img
											src={"/assets/add-icon.png"}
											className={"w-[75%] aspect-square"}
											alt={"Add Social Media"}
											title={"Add Social Media"}
										/>
									</button>
								</div>

								{/* Divider - Header */}
								<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full mt-[1vh]"}/>
							</div>

							{/* Social Media Links List */}
							<div className={"w-full flex-1 overflow-hidden relative rounded-lg"}>
								<div
									className={`w-full h-full flex-1 flex flex-row items-start gap-3
                                        
                                        portrait:p-[1.25rem] portrait:pt-[0.5rem]
                                        portrait:jk:p-[1.75rem] portrait:jk:pt-[0.75rem]
                                        portrait:lg:p-[2.25rem] portrait:lg:pt-[1rem]
    
                                        landscape:p-[0.75rem] landscape:pt-[0.75rem]
                                        landscape:jk:p-[0.9rem] landscape:jk:pt-[0.9rem]
                                        landscape:lg:p-[1.15rem] landscape:lg:pt-[1.15rem]
                                        
                                        overflow-y-hidden overflow-x-scroll flex-nowrap`}
								>
									{
										(!account.socialMediaDetails || account.socialMediaDetails?.length === 0) &&
										<div className={"h-full w-full flex items-center justify-center"}>
											<img
												src={"/assets/ganeshji-logo.png"}
												className={"h-full aspect-square"}
												alt={"GaneshJi"}
												title={"GaneshJi"}
											/>
										</div>
									}

									{/* Icon */}
									{
										account.socialMediaDetails?.map((socialMedia, index) => (
											<div
												key={index}
												className={`h-full aspect-square
                                                    portrait:p-[0.5rem] landscape:p-[0.75rem]
                                                    portrait:jk:p-[1rem] landscape:jk:p-[0.9rem]
                                                    portrait:lg:p-[1.5rem] landscape:lg:p-[1.15rem]
                                                    
                                                    flex items-center gap-4 rounded-lg 
                                                    bg-white border border-[#ECECEC]
                                                    transition-all duration-500 ease-in-out hover:shadow-lg`}
											>
												<div
													className={"h-full flex items-center justify-center aspect-square"}
												>
													<Link
														to={socialMedia.socialMediaLink}
														target={"_blank"}
													>
														<img
															src={handleSocialMediaIcon(socialMedia.socialMediaName)}
															className={"h-full aspect-square"}
															alt={""}
															title={""}
														/>
													</Link>
												</div>

												{/* Edit Button */}
												<button
													onClick={() => {
														setSelectedSocialMediaData(socialMedia);
														setShowEditSocialMediaForm(true);
													}}
													className="aspect-square flex flex-col items-center justify-center
                                                        portrait:w-[1.5rem] portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]

                                                        landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                                        landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]

                                                        bg-[#F5F5F5] rounded-md
                                                        hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4]
                                                        transition duration-300 ease-out"
												>
													<img
														src={"/assets/pencil-edit-icon.png"}
														className={"w-[75%] aspect-square"}
														alt={"Edit Social Media Details"}
														title={"Edit Social Media Details"}
													/>
												</button>
											</div>
										))
									}
									{
										account.socialMediaDetails?.length !== 0 && account.socialMediaDetails?.length <= 2 &&
										<div
											className="w-full h-full
                                            flex-1 flex items-center justify-center"
										>
											<img
												src={"/assets/ganeshji-logo.png"}
												className={"h-full aspect-square"}
												alt={"GaneshJi"}
												title={"GaneshJi"}
											/>
										</div>
									}
								</div>
							</div>
						</div>

						{/* Medical Conditions */}
						<div
							className={`row-span-3 flex flex-col items-start
                            
                                portrait:pt-[1rem] landscape:pt-[0.75rem]
                                portrait:jk:pt-[1.5rem] landscape:jk:pt-[0.9rem]
                                portrait:lg:pt-[2rem] landscape:lg:pt-[1.15rem]
                                
                                rounded-lg bg-white shadow-xl`}
						>
							{/* Medical Conditions - Header */}
							<div
								className="w-full flex flex-col items-start
                                    portrait:px-[1.25rem] landscape:px-[0.75rem]
                                    portrait:jk:px-[1.75rem] landscape:jk:px-[0.9rem]
                                    portrait:lg:p-[2.25rem] landscape:lg:px-[1.15rem]"
							>
								<div className={"w-full flex items-center justify-between"}>
									<p className={"text-[#3D3D3D] text-[2.25vh] font-custom-bold"}>
										{t("PROFILE_PAGE.MEDICAL_CONDITIONS")}
									</p>

									<button
										onClick={() => setShowAddMedicalConditionForm(true)}
										className={`flex items-center justify-center aspect-square
                                            portrait:w-[1.5rem] portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]
                                            
                                            landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                            landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]
                                            
                                            bg-[#D4D4D4] rounded-md shadow-lg 
                                            hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4] 
                                            transition duration-200 ease-out`}
									>
										<img
											src={"/assets/add-icon.png"}
											className={"w-[75%] aspect-square"}
											alt={"Add Medical Condition"}
											title={"Add Medical Condition"}
										/>
									</button>
								</div>

								{/* Divider - Header */}
								<div className={"h-[0.2vh] w-full bg-[#ECECEC] rounded-full mt-[1vh]"}/>
							</div>

							{/* Medical Conditions List */}
							<div
								className="flex-1 flex flex-col items-start gap-3
                                    portrait:p-[1.25rem] portrait:pt-[0.5rem]
                                    portrait:jk:p-[1.75rem] portrait:jk:pt-[0.75rem]
                                    portrait:lg:p-[2.25rem] portrait:lg:pt-[1rem]

                                    landscape:p-[0.75rem] landscape:pt-[0.75rem]
                                    landscape:jk:p-[0.9rem] landscape:jk:pt-[0.9rem]
                                    landscape:lg:p-[1.15rem] landscape:lg:pt-[1.15rem]

                                    overflow-y-scroll thin-scrollbar"
							>
								{
									(!account.medicalConditionDetails || account.medicalConditionDetails?.length === 0) &&
									<div className={"h-full w-full flex items-center justify-center"}>
										<img
											src={"/assets/ganeshji-logo.png"}
											className={"w-[7.5vw] aspect-square"}
											alt={"GaneshJi"}
											title={"GaneshJi"}
										/>
									</div>
								}
								{
									account.medicalConditionDetails?.map((medicalCondition, index) => (
										<div
											key={index}
											className={`h-fit w-full flex items-start 
                                                portrait:gap-[1.75vw] landscape:gap-[0.75vw]
                                                
                                                portrait:p-[0.5rem] landscape:p-[0.75rem]
                                                portrait:jk:p-[1rem] landscape:jk:p-[0.9rem]
                                                portrait:lg:p-[1.5rem] landscape:lg:p-[1.15rem]
                                                
                                                rounded-xl border border-[#ECECEC]`}
										>
											{/* Icon */}
											<div
												className={`flex items-center justify-center aspect-square 
                                                    portrait:h-[3.5rem] landscape:h-[4vw]
                                                    bg-[#F5F5F5] rounded-lg`}
											>
												<img
													src={handleMedicalConditionIcon(medicalCondition.medicalConditionName)}
													className={"w-[80%] aspect-square"}
													alt={
														medicalCondition.medicalConditionName !== "Other"
															? medicalCondition.medicalConditionName
															: medicalCondition.otherMedicalConditionName
													}
													title={
														medicalCondition.medicalConditionName !== "Other"
															? medicalCondition.medicalConditionName
															: medicalCondition.otherMedicalConditionName
													}
												/>
											</div>

											{/* Details */}
											<div className={"w-full flex-1 flex flex-col items-start gap-1.5"}>
												<p className={"text-[#3D3D3D] text-[2vh] leading-none font-custom-bold"}>
													{
														medicalCondition.medicalConditionName !== "Other"
															? medicalCondition.medicalConditionName
															: medicalCondition.otherMedicalConditionName
													}
												</p>
												<p className={"line-clamp-6 overflow-y-scroll thin-scrollbar text-[#838383] text-[1.5vh] leading-tight font-custom-regular"}>
													{medicalCondition.medicalConditionDescription}
												</p>
											</div>

											{/* Edit Button */}
											<button
												onClick={() => {
													setSelectedMedicalConditionData(medicalCondition);
													setShowEditMedicalConditionForm(true);
												}}
												className="aspect-square flex flex-col items-center justify-center
                                                    portrait:w-[1.5rem] portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]

                                                    landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                                                    landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]

                                                    bg-[#F5F5F5] rounded-md
                                                    hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4]
                                                    transition duration-300 ease-out"
											>
												<img
													src={"/assets/pencil-edit-icon.png"}
													className={"w-[75%] aspect-square"}
													alt={"Edit Medical Condition Details"}
													title={"Edit Medical Condition Details"}
												/>
											</button>
										</div>
									))
								}
								{
									account.medicalConditionDetails?.length !== 0 && account.medicalConditionDetails?.length < 3 &&
									<div className={"w-full flex-1 flex items-center justify-center"}>
										<img
											src={"/assets/ganeshji-logo.png"}
											className={"w-[4vw] aspect-square"}
											alt={"GaneshJi"}
											title={"GaneshJi"}
										/>
									</div>
								}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Update PFP Confirmation Dialog */}
			<Modal isOpen={showUpdatePfpConfirmationDialog}>
				<div
					className={`px-[1.5vw] py-[2vh] bg-black/60 rounded-lg
                    transition-all duration-150 ease-out ring-2 ring-white`}
				>
					{/* Header */}
					<h2 className="font-custom-medium text-xl text-white">
						{t("PROFILE_PAGE.PFP.UPDATE.TITLE")}
					</h2>

					{/* Updated PFP */}
					<div className={"relative w-full flex items-center justify-center py-[2vh]"}>
						<img
							className="object-cover drop-shadow-2xl w-[75%] aspect-square rounded-full border-4 border-white"
							src={updatedPfpTempUrl}
							alt="User Profile Image"
						/>
						<button
							className={"w-[2vw] h-[2vw] p-[0.25vh] absolute bottom-[17.5%] right-[17.5%] bg-white shadow-2xl shadow-pink-600 rounded-lg rounded-tr-sm"}
							onClick={() => updatePfpInputRef.current.click()}
						>
							<img
								className="drop-shadow-2xl w-full h-full"
								src={'assets/edit-pfp.png'}
								alt="User Profile Image"
							/>
						</button>
					</div>

					{/* Footer */}
					<div className="mt-[1vh] flex flex-row items-center gap-x-[1vw]">
						<button
							disabled={updatePfpButtonDisabled}
							onClick={() => updatePfp(updatedPfp)}
							className="font-custom-medium inline-flex items-center arrow-button bg-[#D26A53] leading-tight hover:ring-white hover:ring-2 disabled:cursor-not-allowed disabled:bg-slate-400/50 rounded-full pl-[1.25vw] pr-[0.75vw] py-[1vh] text-white transition-all duration-300 ease-in-out"
						>
							{t("PROFILE_PAGE.PFP.UPDATE.OPTION_1")}
							<svg
								viewBox="0 0 6 6"
								fill="none"
								id={"arrow-icon"}
								className={"h-[1vw] w-[1vw] ml-[0.75vw]"}
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M3 1L5 3L3 5"
									id={"arrow-icon-tip"}
									stroke="white"
									strokeLinecap="square"
								/>
								<path
									d="M1 3L4 3"
									id={"arrow-icon-line"}
									stroke="white"
									strokeLinecap="square"
								/>
							</svg>
						</button>
						<button
							disabled={updatePfpButtonDisabled}
							className="inline-flex items-center gap-2 rounded-full ring-inset ring-2 ring-white py-[1vh] px-[2vw] text-SM font-custom-medium text-white focus:outline-none hover:bg-gray-600 focus:outline-1 focus:outline-white transition-all duration-300 ease-in-out"
							onClick={() => {
								updatePfpInputRef.current.value = "";
								setShowUpdatePfpConfirmationDialog(false);
								setUpdatedPfp(null);
								setUpdatedPfpTempUrl("");
							}}
						>
							{t("PROFILE_PAGE.PFP.UPDATE.OPTION_2")}
						</button>
					</div>
				</div>
			</Modal>
		</>
	)
}
